import { isNil } from 'lodash-es'
import { useMemo } from 'react'

import { ConditionalCover, CoreCoverItem, OptionalCover, Quote } from '../domain'
import { ProductConfig } from '../state/configuration'
import { useCoverLevelOption } from './coverOption'

export const useConditionalCovers = (
  quote: Quote | undefined,
  product: ProductConfig,
): readonly ConditionalCover[] => {
  const { coverLevelOption, coverLevelConfig } = useCoverLevelOption(quote, product)

  return useMemo(() => {
    const activeCovers: Set<string> = new Set(
      coverLevelOption?.basePremiumItems.flatMap((it) => it.covers) ?? [],
    )

    const conditionalCoversConfig =
      coverLevelConfig?.covers?.core?.filter((it) => it.type === 'conditional') ?? []

    return (
      conditionalCoversConfig.map(({ code }) => ({
        coverCode: code,
        active: activeCovers.has(code),
      })) ?? []
    )
  }, [quote, product])
}

export const useCoreCovers = (
  quote: Quote | undefined,
  product: ProductConfig,
): readonly CoreCoverItem[] => {
  const { coverLevelOption, coverLevelConfig } = useCoverLevelOption(quote, product)

  return useMemo(() => {
    const activeCovers: Set<string> = new Set(
      coverLevelOption?.basePremiumItems.flatMap((it) => it.covers) ?? [],
    )

    const coreCoversConfig = coverLevelConfig?.covers?.core ?? []

    return coreCoversConfig.map((it): CoreCoverItem => {
      switch (it.type) {
        case 'divider':
          return { type: 'divider' }
        case 'header':
          return { type: 'header', header: it.header }
        case 'included':
          return { type: 'included', coverCode: it.code }
        case 'conditional':
          return {
            type: 'conditional',
            coverCode: it.code,
            active: activeCovers.has(it.code),
          }
      }
    })
  }, [quote, product])
}

export const useOptionalCovers = (
  quote: Quote | undefined,
  product: ProductConfig,
): readonly OptionalCover[] => {
  const { coverLevelOption, coverLevelConfig } = useCoverLevelOption(quote, product)

  return useMemo(() => {
    if (!coverLevelOption) return []

    return (
      coverLevelConfig?.covers?.optional?.map((cover) => {
        const option = coverLevelOption.coverOptionItems.find(
          (item) => item.coverOption === cover.code,
        )

        return {
          coverOption: cover.code,
          available: !isNil(option),
          active: option?.active,
          insurer: option?.insurer,
          premium: option?.premium ?? 0,
          totalPremium: option?.totalPremium ?? 0,
          taxAmount: option?.taxAmount ?? 0,
          contextualHelp: cover.contextualHelp,
          premiumBreakdownLines: option?.premiumBreakdownLines ?? [],
          additionalPremiumBreakdownLines: option?.additionalPremiumBreakdownLines ?? [],
        } satisfies OptionalCover
      }) ?? []
    )
  }, [quote, product])
}
